import { getCartCookie } from "@/utils/cart/getCartCookie";
import { useMarket } from "@app/_context";
import type { Cart } from "@server/gql/graphql";
import { addPromotionToCart } from "@server/requests/checkout/addPromotionToCart";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

type AddPromotion = <T extends boolean = false>(
  discountCode: string,
  async?: T,
) => T extends true ? Promise<Cart | undefined> : void;

export default function useAddPromotion() {
  const { serviceLocale: locale } = useMarket().state.market;
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isError, isPending, isSuccess } = useMutation({
    mutationFn: async (discountCode: string) => {
      const cartId = getCartCookie()?.value;
      if (!cartId) return Promise.resolve(undefined);
      return addPromotionToCart({
        discountCode,
        locale,
        cartId,
      });
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["cart"] });
      await queryClient.cancelQueries({ queryKey: ["checkout"] });
      await queryClient.cancelQueries({ queryKey: ["checkoutDelivery"] });
      await queryClient.cancelQueries({ queryKey: ["payment"] });

      const previousCart = queryClient.getQueryData<Cart>(["cart"]);
      return { previousCart };
    },
    onError: (
      error,
      input,
      context: { previousCart: Cart | undefined } | undefined,
    ) => {
      if (context?.previousCart) {
        queryClient.setQueryData<Cart>(["cart"], context.previousCart);
      }
    },
    onSettled: async () => {
      void queryClient.invalidateQueries({ queryKey: ["cart"] });
      void queryClient.invalidateQueries({ queryKey: ["checkout"] });
      void queryClient.invalidateQueries({ queryKey: ["checkoutDelivery"] });
      await queryClient.invalidateQueries({ queryKey: ["payment"] });
    },
  });

  const addPromotion = useCallback(
    (discountCode: string, async = false) => {
      if (async) return mutateAsync(discountCode);
      mutate(discountCode);
    },
    [mutate, mutateAsync],
  );

  return {
    addPromotion: addPromotion as AddPromotion,
    isErrorAddPromotion: isError,
    isPendingAddPromotion: isPending,
    isSuccessAddPromotion: isSuccess,
  };
}
